import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Pencil, MapPin, Save, X, ImagePlus, Check, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { FollowButton } from '@/components/FollowButton';
import { UserAvatar } from '@/components/UserAvatar';
import { useUserFollow } from '@/hooks/useUserFollow';
import { useFeaturedVehicle } from '@/hooks/useUserVehicles';
import { useAuth } from '@/hooks/useAuth';

interface ProfileHeaderProps {
  userId: string;
  username?: string;
  bio?: string;
  location?: string;
  avatarUrl?: string;
  bannerUrl?: string;
  isOwnProfile: boolean;
  onUpdate?: () => void;
}

export const ProfileHeader = ({
  userId,
  username,
  bio,
  location,
  avatarUrl,
  bannerUrl,
  isOwnProfile,
  onUpdate,
}: ProfileHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedUsername, setEditedUsername] = useState(username || '');
  const [editedBio, setEditedBio] = useState(bio || '');
  const [editedLocation, setEditedLocation] = useState(location || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { followerCount, followingCount } = useUserFollow(userId, user?.id);
  const { data: featuredVehicle } = useFeaturedVehicle(userId);

  // Username format validation (Instagram-style: letters, numbers, underscores, periods)
  const isValidUsernameFormat = (value: string): boolean => {
    if (!value) return true;
    return /^[a-zA-Z0-9_.]+$/.test(value);
  };

  const sanitizeUsername = (value: string): string => {
    // Remove invalid characters, convert to lowercase
    return value.toLowerCase().replace(/[^a-z0-9_.]/g, '');
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeUsername(e.target.value);
    setEditedUsername(sanitized);
  };

  // Check username availability with debounce
  useEffect(() => {
    if (!isEditing) return;
    
    const trimmedUsername = editedUsername.trim();
    
    // If unchanged from original, it's available
    if (trimmedUsername.toLowerCase() === (username || '').toLowerCase()) {
      setUsernameAvailable(true);
      return;
    }
    
    // If empty, don't check
    if (!trimmedUsername) {
      setUsernameAvailable(null);
      return;
    }

    setIsCheckingUsername(true);
    const timer = setTimeout(async () => {
      const { data, error } = await supabase.rpc('check_username_available', {
        check_username: trimmedUsername,
        exclude_user_id: userId,
      });
      
      if (!error) {
        setUsernameAvailable(data);
      }
      setIsCheckingUsername(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [editedUsername, isEditing, username, userId]);

  const handleSave = async () => {
    // Check username availability before saving
    if (usernameAvailable === false) {
      toast({
        title: 'Username unavailable',
        description: 'Please choose a different username.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          username: editedUsername.trim(),
          bio: editedBio,
          location: editedLocation,
        })
        .eq('id', userId);

      if (error) {
        if (error.code === '23505') {
          toast({
            title: 'Username taken',
            description: 'This username is already in use. Please choose another.',
            variant: 'destructive',
          });
          setUsernameAvailable(false);
          return;
        }
        throw error;
      }

      toast({
        title: 'Profile updated',
        description: 'Your profile has been updated successfully.',
      });
      setIsEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedUsername(username || '');
    setEditedBio(bio || '');
    setEditedLocation(location || '');
    setIsEditing(false);
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploadingBanner(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/banner-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-banners')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-banners')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('users')
        .update({ banner_url: publicUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      toast({
        title: 'Banner updated',
        description: 'Your profile banner has been updated.',
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error uploading banner:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload banner. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const displayAvatarUrl = featuredVehicle?.primary_photo_url || avatarUrl;

  return (
    <Card className="overflow-hidden">
      <div className="relative h-32 md:h-48">
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt="Profile banner"
            className="w-full h-full object-cover photo-signature"
          />
        ) : (
          <div className="h-full bg-gradient-to-r from-primary/20 via-primary/10 to-accent/20" />
        )}
        
        {isOwnProfile && (
          <>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              onChange={handleBannerUpload}
              className="absolute w-0 h-0 opacity-0 overflow-hidden"
            />
            <Button
              variant="secondary"
              size="sm"
              className="absolute bottom-3 right-3 opacity-80 hover:opacity-100"
              onClick={() => bannerInputRef.current?.click()}
              disabled={isUploadingBanner}
            >
              {isUploadingBanner ? (
                'Uploading...'
              ) : (
                <>
                  <ImagePlus className="h-4 w-4 mr-2" />
                  {bannerUrl ? 'Change Banner' : 'Add Banner'}
                </>
              )}
            </Button>
          </>
        )}
      </div>
      
      <CardContent className="relative pt-0 pb-6">
        {/* Avatar overlapping the banner */}
        <div className="flex justify-center md:justify-start -mt-16 md:-mt-12 mb-4">
          <UserAvatar
            username={username}
            avatarUrl={displayAvatarUrl}
            size="xl"
            className="border-4 border-background shadow-lg"
            usePhotoFilter={!!featuredVehicle?.primary_photo_url}
          />
        </div>
        
        {/* Profile info on solid background */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex flex-col items-center md:items-start gap-2">
            
            {!isEditing ? (
              <div className="text-center md:text-left space-y-2">
                <h1 className="text-2xl font-bold text-foreground">
                  {username || 'Anonymous User'}
                </h1>
                {location && (
                  <div className="flex items-center justify-center md:justify-start gap-1 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm">{location}</span>
                  </div>
                )}
                {bio && (
                  <p className="text-sm text-muted-foreground max-w-2xl">
                    {bio}
                  </p>
                )}
                <div className="flex items-center justify-center md:justify-start gap-4 text-sm pt-1">
                  <span>
                    <span className="font-semibold text-foreground">{followerCount}</span>
                    <span className="text-muted-foreground ml-1">followers</span>
                  </span>
                  <span>
                    <span className="font-semibold text-foreground">{followingCount}</span>
                    <span className="text-muted-foreground ml-1">following</span>
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-4 w-full max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <p className="text-xs text-muted-foreground">Letters, numbers, underscores, and periods only</p>
                  <div className="relative">
                    <Input
                      id="username"
                      value={editedUsername}
                      onChange={handleUsernameChange}
                      placeholder="your_username"
                      className={usernameAvailable === false ? 'border-destructive pr-10' : 'pr-10'}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isCheckingUsername && (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                      {!isCheckingUsername && usernameAvailable === true && editedUsername.trim() && (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                      {!isCheckingUsername && usernameAvailable === false && (
                        <X className="h-4 w-4 text-destructive" />
                      )}
                    </div>
                  </div>
                  {usernameAvailable === false && (
                    <p className="text-sm text-destructive">This username is already taken</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={editedLocation}
                    onChange={(e) => setEditedLocation(e.target.value)}
                    placeholder="e.g., San Diego, CA"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={editedBio}
                    onChange={(e) => setEditedBio(e.target.value)}
                    placeholder="Tell us about yourself and your rig..."
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {isOwnProfile ? (
              !isEditing ? (
                <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button
                    onClick={handleCancel}
                    disabled={isSaving}
                    variant="outline"
                    size="sm"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </>
              )
            ) : (
              <FollowButton userId={userId} currentUserId={user?.id} />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
